/**
 * `?showcase=dialogue` — exercises DialogueRunner (conditions, choices, marks/flags, read-skip,
 * typewriter, Shift fast-forward), CinematicPlayer (parallel/label/goto/skip on Esc or X) and a
 * MemoryStage demo, on a self-contained StoryState so the real save is never touched.
 */
import Phaser from 'phaser';
import type { IActor } from '@/core/types/contracts';
import type { WeatherState } from '@/core/types';
import { Depth, FONT_BODY, GAME_H, GAME_W } from '@/core/config';
import { ctxOf } from '@/core/context';
import { StoryState } from '@/story/flags';
import { createActor } from '@/characters';
import { audio } from '@/audio';
import { DialogueRunner, type DialogueHooks } from '../DialogueRunner';
import { resolveDialogueBox } from '../DevDialogueBox';
import { registerDialogues } from '../registry';
import { isReadMark } from '../hash';
import { CinematicPlayer, type CinematicResult } from '@/cinematics/CinematicPlayer';
import { registerTimelines } from '@/cinematics/registry';
import { createSceneCinematicHost, type CinematicStateAccess } from '@/cinematics/SceneHost';
import { MemoryStage, isMemoryStageRunning, registerMemoryStages, runMemoryStage } from '@/cinematics/MemoryStage';
import { SHOWCASE_DIALOGUES, SHOWCASE_TIMELINES, ZHAO, ZICHAO } from './data';

const WEATHERS: WeatherState[] = ['sultry', 'wind', 'drizzle', 'rain', 'interior-rain'];
const HOME = { [ZICHAO]: { x: 220, y: 200 }, [ZHAO]: { x: 330, y: 200 } } as const;
const MEMORY_SCENE_KEY = 'MemoryStage:showcase';

export interface DialogueShowcaseApi {
  runner: DialogueRunner;
  player: CinematicPlayer;
  state: StoryState;
  weather(): string;
  lastResult(): string;
  runDialogue(): Promise<void>;
  playTimeline(): Promise<CinematicResult>;
  runMemory(): Promise<CinematicResult>;
  skip(): boolean;
  /** Id of the nested `play` timeline currently running inside the timeline, if any. */
  nestedTimeline(): string | null;
  /** True while a dialogue, timeline or memory stage owns the showcase (key presses are ignored). */
  busy(): boolean;
  /** Test hook: set the showcase-local weather (same as cycling with W). */
  setWeather(state: WeatherState): void;
  history(): readonly { speaker?: string; text: string }[];
  historyTexts(): string[];
  boxText(): string;
  actorPos(id: string): { x: number; y: number; facing: string } | null;
}
declare global { interface Window { __storyShowcase?: DialogueShowcaseApi } }

class ShowcaseMemoryStage extends MemoryStage {
  constructor() { super(MEMORY_SCENE_KEY); }
  protected override buildStage(): void {
    this.add.rectangle(0, 150, GAME_W, 90, 0x223048).setOrigin(0).setDepth(Depth.GROUND);
    this.add.text(GAME_W / 2, 120, '甲板 · 黑夜 · 风浪  [dev]', { fontFamily: FONT_BODY, fontSize: '12px', color: '#9fb2c4' }).setOrigin(0.5).setDepth(Depth.UI);
    this.spawnActor(ZICHAO, ZICHAO, 200, 220, 'right');
  }
}

export function runDialogueShowcase(scene: Phaser.Scene): void {
  const ctx = ctxOf(scene);
  registerDialogues(SHOWCASE_DIALOGUES);
  registerTimelines(SHOWCASE_TIMELINES);
  registerMemoryStages([{ key: 'showcase-memory', timeline: 'showcase-memory', sceneKey: MEMORY_SCENE_KEY, sceneClass: ShowcaseMemoryStage, style: { label: '黑夜风浪（showcase）' } }]);

  // ---- self-contained state (never touches ctx.story / saves); starts at TITLE like a new game
  const state = new StoryState(undefined, { emit: false });
  state.advance('TITLE');
  let weather: WeatherState = ctx.url.weather ?? 'sultry';
  let lastResult = '—';
  const stateAccess: CinematicStateAccess = {
    hasFlag: (f) => state.has(f as never),
    hasMark: (m) => state.hasMark(m),
    setMark: (m) => void state.mark(m),
    setFlag: (f) => void state.advance(f as never),
  };

  // ---- stage
  scene.cameras.main.setBackgroundColor(0x101418);
  scene.cameras.main.setBounds(0, 0, GAME_W, GAME_H);
  scene.add.rectangle(0, 40, GAME_W, 190, 0x1f3d3a).setOrigin(0).setDepth(Depth.GROUND);
  scene.add.rectangle(0, 150, GAME_W, 4, 0x2c5450).setOrigin(0).setDepth(Depth.GROUND_DETAIL);
  scene.add.rectangle(60, 100, 60, 40, 0x8a5a32).setOrigin(0).setDepth(Depth.WORLD_BASE + 140);
  scene.add.text(90, 96, '货箱', { fontFamily: FONT_BODY, fontSize: '10px', color: '#e2c48c' }).setOrigin(0.5, 1).setDepth(Depth.WORLD_BASE + 141);
  const actors = new Map<string, IActor>();
  const labels: { actor: IActor; text: Phaser.GameObjects.Text }[] = [];
  const spawn = (id: string, x: number, y: number) => {
    const a = createActor(scene, id, id, x, y, 'down');
    actors.set(id, a);
    labels.push({ actor: a, text: scene.add.text(x, y - 52, a.displayName, { fontFamily: FONT_BODY, fontSize: '11px', color: '#ffffff' }).setOrigin(0.5, 1).setDepth(Depth.OVERHEAD) });
    return a;
  };
  spawn(ZICHAO, HOME[ZICHAO].x, HOME[ZICHAO].y);
  spawn(ZHAO, HOME[ZHAO].x, HOME[ZHAO].y);

  // ---- dialogue + cinematics
  const box = resolveDialogueBox(scene, { charsPerSec: () => ({ slow: 15, normal: 30, fast: 60, instant: 1000 })[ctx.settings.textSpeed] });
  const hooks: DialogueHooks = {
    hasFlag: (f) => state.has(f as never),
    hasMark: (m) => state.hasMark(m),
    setMark: (m) => void state.mark(m),
    setFlag: (f) => void state.advance(f as never),
    weather: () => weather,
    instant: () => ctx.settings.textSpeed === 'instant',
  };
  const runner = new DialogueRunner(box, hooks, {
    host: {
      face: (id, facing) => actors.get(id)?.setFacing(facing),
      emote: (id, emote) => void actors.get(id)?.showEmote(emote, 600),
      sfx: (k) => audio().sfx(k),
      wait: (ms) => new Promise<void>((r) => { scene.time.delayedCall(ms, r); }),
    },
  });
  const host = createSceneCinematicHost(scene, { dialogue: runner, ctx, actors, state: stateAccess });
  // route weather through the showcase's local state instead of ctx (no side effects on the real game)
  host.weather = (st) => { weather = st; };
  const player = new CinematicPlayer(host);

  // ---- HUD
  const title = scene.add.text(8, 6, '[dev] 对话 / 演出 showcase   1 对话   2 演出   3 回忆舞台   S 跳过已读   W 天气   R 重置   Esc/X 跳过演出   Enter/Space/点击 推进   按住 Shift 快进', { fontFamily: FONT_BODY, fontSize: '11px', color: '#e2c48c', wordWrap: { width: GAME_W - 16 } }).setDepth(Depth.UI).setScrollFactor(0);
  const status = scene.add.text(GAME_W - 8, 44, '', { fontFamily: FONT_BODY, fontSize: '10px', color: '#c9b89a', align: 'left', lineSpacing: 2, backgroundColor: '#0b0f14cc', padding: { x: 6, y: 4 } }).setOrigin(1, 0).setDepth(Depth.UI).setScrollFactor(0);
  const refresh = () => {
    const marks = state.snapshot().marks;
    status.setText([
      `阶段：${runner.getPhase()}  对话：${runner.currentDialogue() ?? '—'}${runner.isFastHeld() ? '  [快进]' : ''}`,
      `演出：${player.isPlaying() ? player.currentTimeline() : '—'}${player.nestedPlayer() ? `  ▸ 嵌套 ${player.nestedPlayer()?.currentTimeline()}` : ''}  结果：${lastResult}`,
      `旗标：${state.snapshot().flags.join(' > ') || '（无）'}`,
      `标记：${marks.filter((m) => !isReadMark(m)).join(', ') || '（无）'}`,
      `已读：${marks.filter(isReadMark).length} 行  跳过已读：${runner.skipReadLines ? '开' : '关'}`,
      `天气：${weather}  文字速度：${ctx.settings.textSpeed}`,
    ]);
    for (const l of labels) l.text.setPosition(l.actor.x, l.actor.y - 52);
  };
  scene.events.on(Phaser.Scenes.Events.UPDATE, refresh);

  // ---- actions
  const busy = () => runner.isRunning() || player.isPlaying() || isMemoryStageRunning(scene.scene, MEMORY_SCENE_KEY);
  const runDialogue = async () => { if (busy()) return; await runner.run('showcase-branch'); lastResult = '对话结束'; };
  const playTimeline = async () => { if (busy()) return 'completed' as const; const r = await player.play('showcase-scene'); lastResult = `演出 ${r}`; scene.cameras.main.centerOn(GAME_W / 2, GAME_H / 2); return r; };
  const runMemory = async () => { if (busy()) return 'completed' as const; const r = await runMemoryStage(scene, 'showcase-memory', 'showcase-memory', { state: stateAccess }); lastResult = `回忆 ${r}`; return r; };
  const reset = () => {
    if (busy()) return;
    state.reset(); state.advance('TITLE'); weather = 'sultry'; lastResult = '已重置';
    for (const [id, a] of actors) { const h = HOME[id as keyof typeof HOME]; if (h) a.setPosition(h.x, h.y, 'down'); }
    scene.cameras.main.centerOn(GAME_W / 2, GAME_H / 2);
  };
  // Esc/X skip the innermost running timeline (a nested `play` child owns the skip while it runs — same
  // scope the core's ctx.skipHandler stack gives Esc); the outer scene continues after the child.
  const skip = () => (player.nestedPlayer() ?? player).skip();

  const kb = scene.input.keyboard;
  const advance = () => runner.advance();
  kb?.on('keydown-ONE', () => void runDialogue());
  kb?.on('keydown-TWO', () => void playTimeline());
  kb?.on('keydown-THREE', () => void runMemory());
  kb?.on('keydown-S', () => { runner.skipReadLines = !runner.skipReadLines; });
  kb?.on('keydown-W', () => { weather = WEATHERS[(WEATHERS.indexOf(weather) + 1) % WEATHERS.length] as WeatherState; });
  kb?.on('keydown-R', reset);
  kb?.on('keydown-ESC', skip);
  kb?.on('keydown-X', skip);
  kb?.on('keydown-ENTER', advance);
  kb?.on('keydown-SPACE', advance);
  kb?.on('keydown-Z', advance);
  kb?.on('keydown-SHIFT', () => runner.setFastHeld(true));
  kb?.on('keyup-SHIFT', () => runner.setFastHeld(false));
  scene.input.on('pointerdown', advance);

  const history = () => ('getHistory' in box ? (box as { getHistory(): readonly { speaker?: string; text: string }[] }).getHistory() : []);
  window.__storyShowcase = {
    runner, player, state,
    weather: () => weather,
    setWeather: (st) => { weather = st; },
    lastResult: () => lastResult,
    runDialogue, playTimeline, runMemory, skip,
    nestedTimeline: () => player.nestedPlayer()?.currentTimeline() ?? null,
    busy,
    history,
    historyTexts: () => history().map((h) => h.text),
    boxText: () => ('currentText' in box ? (box as { currentText(): string }).currentText() : ''),
    actorPos: (id) => { const a = actors.get(id); return a ? { x: a.x, y: a.y, facing: a.facing } : null; },
  };

  scene.events.once(Phaser.Scenes.Events.SHUTDOWN, () => {
    scene.events.off(Phaser.Scenes.Events.UPDATE, refresh);
    kb?.removeAllListeners();
    scene.input.removeAllListeners();
    runner.abort();
    for (const a of actors.values()) a.destroy();
    actors.clear();
    title.destroy();
    delete window.__storyShowcase;
  });
}
